import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';

import {
    calculateElevationProfile,
    calculateElevationStats,
    downsampleProfile,
    formatElevation,
    formatDistance,
} from '../../lib/elevationUtils';

// Find elevation at a given distance along the profile
function getElevationAtDistance(profile, distance) {
    if (!profile || profile.length === 0 || distance == null) return null;

    // Find the two profile points that bracket this distance
    for (let i = 1; i < profile.length; i++) {
        if (profile[i].distance >= distance) {
            const prev = profile[i - 1];
            const curr = profile[i];

            // Interpolate elevation between the two points
            const segmentLength = curr.distance - prev.distance;
            if (segmentLength <= 0) return curr.elevation;

            const t = (distance - prev.distance) / segmentLength;
            const elevation = prev.elevation + (curr.elevation - prev.elevation) * t;
            return elevation;
        }
    }

    // Distance is beyond the profile - return last elevation
    return profile[profile.length - 1].elevation;
}

@observer
class ElevationProfile extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hoveredPoint: null,
            collapsed: false,
        };
        this.svgRef = null;
    }

    handleMouseMove = (e) => {
        if (!this.svgRef) return;

        const { profile } = this.props;
        if (!profile || profile.length === 0) return;

        const rect = this.svgRef.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const width = rect.width;

        // ViewBox dimensions (must match render method)
        const viewBoxWidth = 1000;
        const viewBoxPaddingLeft = 60;
        const viewBoxPaddingRight = 20;

        // Scale padding from viewBox units to screen pixels
        const scale = width / viewBoxWidth;
        const paddingLeft = viewBoxPaddingLeft * scale;
        const paddingRight = viewBoxPaddingRight * scale;
        const chartWidth = width - paddingLeft - paddingRight;

        // Calculate relative position within chart area
        const relativeX = (x - paddingLeft) / chartWidth;

        if (relativeX < 0 || relativeX > 1) {
            this.setState({ hoveredPoint: null });
            return;
        }

        const maxDistance = profile[profile.length - 1]?.distance || 0;
        const targetDistance = relativeX * maxDistance;

        // Find closest point
        let closestPoint = profile[0];
        let closestDiff = Math.abs(profile[0].distance - targetDistance);

        for (const point of profile) {
            const diff = Math.abs(point.distance - targetDistance);
            if (diff < closestDiff) {
                closestDiff = diff;
                closestPoint = point;
            }
        }

        this.setState({ hoveredPoint: closestPoint });

        // Notify parent of hovered point
        const { onHover } = this.props;
        if (onHover) {
            onHover(closestPoint);
        }
    }

    handleMouseLeave = () => {
        this.setState({ hoveredPoint: null });

        // Notify parent that hover ended
        const { onHover } = this.props;
        if (onHover) {
            onHover(null);
        }
    }

    handleTouchStart = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleTouchMove(e);
    }

    handleTouchMove = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!e.touches || e.touches.length === 0) return;

        const touch = e.touches[0];
        this.handleMouseMove({ clientX: touch.clientX, clientY: touch.clientY });
    }

    handleTouchEnd = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleMouseLeave();
    }

    handleClick = (e) => {
        const { hoveredPoint } = this.state;
        const { onPointClick } = this.props;

        if (hoveredPoint && onPointClick) {
            onPointClick(hoveredPoint);
        }
    }

    toggleCollapsed = () => {
        this.setState({ collapsed: !this.state.collapsed });
    }

    render() {
        const {
            profile,
            height: propHeight,
            lineColor = '#ffc107',
            stores,
            currentPosition,
            trackers = [],
            totalTrackLength = 0,
        } = this.props;
        const { hoveredPoint, collapsed } = this.state;

        // Responsive height based on screen width
        const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
        let height = propHeight;
        if (!height) {
            if (windowWidth < 600) {
                height = 100; // Small mobile - reduced for better proportions
            } else if (windowWidth < 900) {
                height = 120; // Mobile/tablet portrait
            } else if (windowWidth < 1200) {
                height = 180; // Tablet/small desktop
            } else if (windowWidth < 1600) {
                height = 200; // Medium desktop
            } else {
                height = 220; // Large/wide screens
            }
        }

        const { userStore } = stores || {};
        const { user = {} } = userStore || {};
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        if (!profile || profile.length === 0) {
            return null;
        }

        // Calculate bounds
        const elevations = profile.map(p => p.elevation).filter(e => e > 0);
        if (elevations.length === 0) {
            return null;
        }

        const minElev = Math.min(...elevations);
        const maxElev = Math.max(...elevations);
        const profileMaxDist = profile[profile.length - 1]?.distance || 0;

        // Use backend's totalTrackLength if available (ensures tracker positions align with profile)
        const maxDist = totalTrackLength > 0 ? totalTrackLength : profileMaxDist;

        // Calculate scale factor to align profile distances with backend track distances
        const scaleFactor = (totalTrackLength > 0 && profileMaxDist > 0) ? totalTrackLength / profileMaxDist : 1;

        // Check for bad data
        if (!isFinite(minElev) || !isFinite(maxElev) || !isFinite(maxDist) || maxDist <= 0) {
            return null;
        }

        // Add padding to elevation range
        const elevRange = maxElev - minElev || 100;
        const elevPadding = elevRange * 0.15; // Moderate padding for balanced look
        const chartMinElev = Math.max(0, minElev - elevPadding);
        const chartMaxElev = maxElev + elevPadding;
        const chartElevRange = chartMaxElev - chartMinElev;

        // Fixed visual parameters
        const lineWidth = 3;
        const fillOpacity = 0.3;
        const fontSize = 14;
        const dotRadius = 6;
        const dotStroke = 2;
        const labelFontSize = 10;
        const gridStrokeWidth = 1;

        // SVG dimensions - use viewBox for responsive scaling
        const svgHeight = collapsed ? 40 : height;
        const viewBoxWidth = 1000;
        const viewBoxHeight = svgHeight;
        const paddingLeft = 60;
        const paddingRight = 20;
        const paddingTop = 20;
        const paddingBottom = 30;
        const chartWidth = viewBoxWidth - paddingLeft - paddingRight;
        const chartHeight = viewBoxHeight - paddingTop - paddingBottom;

        // Generate path points (scale distances to match backend's totalTrackLength)
        const pathPoints = profile.map((p) => {
            const scaledDistance = p.distance * scaleFactor;
            const x = paddingLeft + (scaledDistance / maxDist) * chartWidth;
            const y = paddingTop + (1 - (p.elevation - chartMinElev) / chartElevRange) * chartHeight;
            return { x, y, point: p, scaledDistance };
        });

        // Create SVG path
        const pathD = pathPoints.map((p, i) =>
            `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
        ).join(' ');

        // Create fill path (area under line)
        const fillPathD = pathD +
            ` L ${paddingLeft + chartWidth} ${paddingTop + chartHeight}` +
            ` L ${paddingLeft} ${paddingTop + chartHeight} Z`;

        // Hovered point marker (scale distance to match backend's totalTrackLength)
        let hoverMarkerX = null;
        let hoverMarkerY = null;
        if (hoveredPoint && maxDist > 0) {
            const scaledHoverDistance = hoveredPoint.distance * scaleFactor;
            hoverMarkerX = paddingLeft + (scaledHoverDistance / maxDist) * chartWidth;
            hoverMarkerY = paddingTop + (1 - (hoveredPoint.elevation - chartMinElev) / chartElevRange) * chartHeight;
        }

        // Calculate tracker positions on the profile using distance traveled
        const trackerPositions = trackers
            .filter(t => t && t.totalDistanceOnTrack != null && t.totalDistanceOnTrack >= 0)
            .map((tracker) => {
                const distance = tracker.totalDistanceOnTrack;

                // Skip if distance is beyond the track or invalid
                if (distance > maxDist * 1.1 || distance < 0) return null; // Allow 10% tolerance

                // Get elevation at this distance along the profile
                const elevation = getElevationAtDistance(profile, distance);
                if (elevation == null) return null;

                const x = paddingLeft + (distance / maxDist) * chartWidth;
                const y = paddingTop + (1 - (elevation - chartMinElev) / chartElevRange) * chartHeight;

                return {
                    x,
                    y,
                    color: tracker.lineColor || tracker.bgColor || '#ff0000',
                    bib: tracker.contestant?.bib,
                    name: tracker.contestant?.lastname || tracker.trackerId,
                    elevation,
                    distance,
                };
            })
            .filter(t => t !== null);

        const bgColor = darkmode ? '#1a1a2e' : '#f8f9fa';
        const textColor = darkmode ? '#e0e0e0' : '#333';
        const gridColor = darkmode ? '#444' : '#ddd';

        // Calculate hover tooltip position - use chart percentage (0-100% of chart area)
        const chartPercentage = hoveredPoint ? ((hoveredPoint.distance * scaleFactor) / maxDist) * 100 : 0;
        // Determine tooltip alignment based on position to prevent overflow
        const isNearRightEdge = chartPercentage > 80;
        const isNearLeftEdge = chartPercentage < 20;

        return (
            <div
                class={`elevation-profile ${darkmode ? 'bg-dark' : 'bg-light'}`}
                style={{
                    borderTop: `1px solid ${gridColor}`,
                    borderRadius: '10px 10px 0 0',
                    position: 'relative',
                }}
            >
                {/* Header */}
                <div
                    class='d-flex justify-content-between align-items-center px-2 py-2'
                    style={{ cursor: 'pointer', borderBottom: `1px solid ${gridColor}`, minHeight: '44px' }}
                    onClick={this.toggleCollapsed}
                >
                    <div class='d-flex align-items-center'>
                        <span class='small font-weight-bold' style={{ color: textColor }}>
                            <i class='fa-solid fa-mountain me-2' />
                            Elevation Profile
                        </span>
                        {!collapsed && trackerPositions.length > 0 && (
                            <span
                                class='small ms-2'
                                style={{ color: darkmode ? '#888' : '#999', fontStyle: 'italic', fontSize: '0.7em' }}
                            >
                                (positions are approximate)
                            </span>
                        )}
                    </div>
                    <span class='small' style={{ color: textColor }}>
                        <i class={`fa-solid fa-chevron-${collapsed ? 'down' : 'up'}`} />
                    </span>
                </div>

                {/* Hover info */}
                {!collapsed && hoveredPoint && (
                    <div
                        class='position-absolute bg-dark text-white px-2 py-1 rounded small'
                        style={{
                            left: isNearRightEdge ? 'auto' : isNearLeftEdge ? '70px' : `calc(${chartPercentage * 0.92}% + 60px)`,
                            right: isNearRightEdge ? '30px' : 'auto',
                            top: '40px',
                            transform: isNearLeftEdge || isNearRightEdge ? 'none' : 'translateX(-50%)',
                            zIndex: 10,
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                        }}
                    >
                        {formatElevation(hoveredPoint.elevation)} | {formatDistance(hoveredPoint.distance)}
                    </div>
                )}

                {/* Chart */}
                {!collapsed && (
                    <svg
                        ref={el => this.svgRef = el}
                        width='100%'
                        height={svgHeight}
                        viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
                        preserveAspectRatio='none'
                        style={{ display: 'block', cursor: 'crosshair', touchAction: 'none' }}
                        onMouseMove={this.handleMouseMove}
                        onMouseLeave={this.handleMouseLeave}
                        onClick={this.handleClick}
                        onTouchStart={this.handleTouchStart}
                        onTouchMove={this.handleTouchMove}
                        onTouchEnd={this.handleTouchEnd}
                    >
                        {/* Background */}
                        <rect width={viewBoxWidth} height={viewBoxHeight} fill={bgColor} />

                        {/* Grid lines */}
                        {[0, 0.25, 0.5, 0.75, 1].map(ratio => (
                            <line
                                key={`grid-h-${ratio}`}
                                x1={paddingLeft}
                                y1={paddingTop + ratio * chartHeight}
                                x2={paddingLeft + chartWidth}
                                y2={paddingTop + ratio * chartHeight}
                                stroke={gridColor}
                                strokeWidth={gridStrokeWidth}
                                strokeDasharray='4,4'
                            />
                        ))}

                        {/* Y-axis labels */}
                        <text
                            x={paddingLeft - 5}
                            y={paddingTop + 10}
                            textAnchor='end'
                            fill={textColor}
                            fontSize={fontSize}
                        >
                            {Math.round(chartMaxElev)}m
                        </text>
                        <text
                            x={paddingLeft - 5}
                            y={paddingTop + chartHeight}
                            textAnchor='end'
                            fill={textColor}
                            fontSize={fontSize}
                        >
                            {Math.round(chartMinElev)}m
                        </text>

                        {/* X-axis labels */}
                        <text
                            x={paddingLeft}
                            y={viewBoxHeight - 8}
                            textAnchor='start'
                            fill={textColor}
                            fontSize={fontSize}
                        >
                            0 km
                        </text>
                        <text
                            x={paddingLeft + chartWidth}
                            y={viewBoxHeight - 8}
                            textAnchor='end'
                            fill={textColor}
                            fontSize={fontSize}
                        >
                            {(maxDist / 1000).toFixed(1)} km
                        </text>

                        {/* Fill area */}
                        <path
                            d={fillPathD}
                            fill={lineColor}
                            fillOpacity={fillOpacity}
                        />

                        {/* Line */}
                        <path
                            d={pathD}
                            fill='none'
                            stroke={lineColor}
                            strokeWidth={lineWidth}
                        />

                        {/* Tracker positions */}
                        {trackerPositions.map((tracker, idx) => (
                            <g key={`tracker-${idx}`}>
                                {/* Vertical line from tracker to x-axis */}
                                <line
                                    x1={tracker.x}
                                    y1={tracker.y}
                                    x2={tracker.x}
                                    y2={paddingTop + chartHeight}
                                    stroke={tracker.color}
                                    strokeWidth={gridStrokeWidth}
                                    strokeOpacity='0.5'
                                />
                                {/* Tracker dot */}
                                <circle
                                    cx={tracker.x}
                                    cy={tracker.y}
                                    r={dotRadius}
                                    fill={tracker.color}
                                    stroke='#fff'
                                    strokeWidth={dotStroke}
                                />
                                {/* Bib number label */}
                                {tracker.bib && (
                                    <text
                                        x={tracker.x}
                                        y={tracker.y - dotRadius - 4}
                                        textAnchor='middle'
                                        fill={textColor}
                                        fontSize={labelFontSize}
                                        fontWeight='bold'
                                    >
                                        {tracker.bib}
                                    </text>
                                )}
                            </g>
                        ))}

                        {/* Hover marker */}
                        {hoverMarkerX !== null && (
                            <>
                                <line
                                    x1={hoverMarkerX}
                                    y1={paddingTop}
                                    x2={hoverMarkerX}
                                    y2={paddingTop + chartHeight}
                                    stroke={textColor}
                                    strokeWidth='2'
                                    strokeDasharray='6,6'
                                />
                                <circle
                                    cx={hoverMarkerX}
                                    cy={hoverMarkerY}
                                    r='8'
                                    fill={lineColor}
                                    stroke='white'
                                    strokeWidth='3'
                                />
                            </>
                        )}
                    </svg>
                )}
            </div>
        );
    }
}

export default ElevationProfile;
